<template>
  <div class="s-setting-container no-border">
    <div class="flex-row just-between">
      <div class="sac__font-20 semi-bold">
        Billing
      </div>
      <SaveCancelSection
        v-if="adminRole === 'admin'"
        :cancel="resetPartnerConfig"
        :save="updatePartnerConfig"
        :disabled="!changeCheck"
      />
    </div>
    <BillingProfile
      :defaultPayment="defaultPayment"
      :screen="screen"
      :corpId="corpId"
      :taxExempt="taxExempt"
      :taxNumber="taxNumber"
      :expireDate="expireDate"
      :reasonCode="reasonCode"
      :taxDoc="taxDoc"
      :pdfFileName="pdfFileName"
      :taxDocUrl="taxDocUrl"
      :categoryList="taxExemptCategory"
      :selectedPaymentMethod="selectedPaymentMethod"
      :loadingPaymentMethod="loadingPaymentMethod"
      :onChange="handleChange"
      :errorObj="errorObj"
      :change="change"
      :toggleActionModal="toggleActionModal"
      :invoiceNote="invoiceNote"
    />
    <ProcessOverlay :show="updating" text="Saving" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import partnerSettingsMixin from '@shared/mixins/partnerSettingsMixin';
import BillingProfile from '../../partnerSettings/billing/BillingProfile.vue';
import ProcessOverlay from '../../ProcessOverlay.vue';
import SaveCancelSection from '../shared/SaveCancelSection.vue';

export default {
  name: 'BillingSettingSection',
  components: {
    BillingProfile,
    ProcessOverlay,
    SaveCancelSection,
  },
  mixins: [partnerSettingsMixin],
  props: {
    screen: String,
    corpId: Number,
    change: Function,
    partnerSettings: Object,
    taxExemptCategory: Array,
    update: Function,
    selectedPaymentMethod: Array,
    loadingPaymentMethod: Boolean,
    toggleActionModal: Function,
  },
  data() {
    const {
      TAX_EXEMPT_EXPIRATION = '',
      TAX_EXEMPT_NUMBER = '',
      TAX_EXEMPT_REASON = '',
      TAX_EXEMPT_DOCUMENT = '',
      TAX_EXEMPT = false,
      TAX_DOC = null,
    } = this.partnerSettings;
    const {
      primary_payment_source_id: defaultPayment,
      invoice_notes: invoiceNote,
    } = this.$store.getters.chargebeeCustomer;
    return {
      expireDate: TAX_EXEMPT_EXPIRATION,
      taxNumber: TAX_EXEMPT_NUMBER,
      reasonCode: TAX_EXEMPT_REASON,
      taxExempt: TAX_EXEMPT,
      taxDocUrl: TAX_EXEMPT_DOCUMENT,
      taxDoc: TAX_DOC,
      pdfFileName: null,
      invoiceNote,
      defaultPayment,
      errorObj: {},
      updating: false,
    };
  },
  computed: {
    ...mapGetters([
      'adminRole',
    ]),
    changeCheck() {
      const {
        TAX_EXEMPT_EXPIRATION = '',
        TAX_EXEMPT_NUMBER = '',
        TAX_EXEMPT_REASON = '',
        TAX_EXEMPT_DOCUMENT = '',
        TAX_EXEMPT = false,
        TAX_DOC = null,
      } = this.partnerSettings;
      const {
        primary_payment_source_id: defaultPayment,
        invoice_notes: invoiceNote,
      } = this.$store.getters.chargebeeCustomer;
      let changes = false;
      if (TAX_EXEMPT_EXPIRATION !== this.expireDate) {
        changes = true;
      }
      if (TAX_EXEMPT_NUMBER !== this.taxNumber) {
        changes = true;
      }
      if (TAX_EXEMPT_REASON !== this.reasonCode) {
        changes = true;
      }
      if (TAX_EXEMPT_DOCUMENT !== this.taxDocUrl) {
        changes = true;
      }
      if (TAX_EXEMPT !== this.taxExempt) {
        changes = true;
      }
      if (TAX_DOC !== this.taxDoc) {
        changes = true;
      }
      if (defaultPayment !== this.defaultPayment) {
        changes = true;
      }
      if (invoiceNote !== this.invoiceNote) {
        changes = true;
      }
      if (!changes) {
        this.changePartnerSettingsValue('showWarningOnExit', false);
      } else if (changes && !this.showWarningOnExit && !this.warningTab) {
        this.changePartnerSettingsValue('showWarningOnExit', true);
      }
      return changes;
    },
  },
  methods: {
    handleChange(field, value) {
      this[field] = value;
    },
    resetPartnerConfig() {
      const {
        TAX_EXEMPT_EXPIRATION = '',
        TAX_EXEMPT_NUMBER = '',
        TAX_EXEMPT_REASON = '',
        TAX_EXEMPT = false,
        TAX_EXEMPT_DOCUMENT = '',
        TAX_DOC = null,
      } = this.partnerSettings;
      this.expireDate = TAX_EXEMPT_EXPIRATION;
      this.taxNumber = TAX_EXEMPT_NUMBER;
      this.reasonCode = TAX_EXEMPT_REASON;
      this.taxDocUrl = TAX_EXEMPT_DOCUMENT;
      this.taxDoc = TAX_DOC;
      this.pdfFileName = null;
      this.taxExempt = TAX_EXEMPT;
      this.errorObj = {};
      const {
        primary_payment_source_id: defaultPayment,
        invoice_notes: invoiceNote,
      } = this.$store.getters.chargebeeCustomer;
      this.defaultPayment = defaultPayment;
      this.invoiceNote = invoiceNote;
    },
    async updatePartnerConfig() {
      const {
        expireDate,
        taxNumber,
        reasonCode,
        taxExempt,
        taxDoc,
        taxDocUrl,
        defaultPayment,
        invoiceNote,
      } = this;
      const {
        primary_payment_source_id: oldDefaultPayment,
        invoice_notes: oldInvoiceNote,
      } = this.$store.getters.chargebeeCustomer;
      const errorObj = {};
      this.changePartnerSettingsValue('showWarningOnExit', false);
      if (oldDefaultPayment !== defaultPayment) {
        this.$store.dispatch('updateDefaultPaymentMethod', defaultPayment);
      }
      if (oldInvoiceNote !== invoiceNote) {
        this.$store.dispatch('updateInvoiceNote', { note: invoiceNote, corpId: this.corpId });
      }
      if (taxExempt) {
        if (!expireDate) {
          errorObj.exemptExpErr = 'Expiration date is required.';
        }
        if (!taxNumber) {
          errorObj.exemptDocErr = 'Tax number is required.';
        }
        if (!reasonCode) {
          errorObj.exemptReasonErr = 'Exempt reason is required.';
        }
        if (!taxDocUrl && !taxDoc) {
          errorObj.taxDocErr = 'Tax Document is required.';
        }
      }
      this.errorObj = errorObj;
      if (Object.keys(errorObj).length === 0) {
        this.updating = true;
        if (taxDoc) {
          const formData = new FormData();
          formData.append('taxExempt', taxExempt);
          formData.append('taxDoc', taxDoc);
          formData.append('reasonCode', reasonCode);
          formData.append('taxNumber', taxNumber);
          formData.append('expireDate', expireDate);
          formData.append('corpId', this.corpId);
          await this.$store.dispatch('setTaxExempt', formData);
          if (this.screen === 'partnerDetail') {
            this.$store.dispatch('changePartnerDetailInfo', {
              field: 'partnerSettings',
              value: {
                ...this.partnerSettings,
                TAX_EXEMPT_EXPIRATION: expireDate,
                TAX_EXEMPT_NUMBER: taxNumber,
                TAX_EXEMPT_REASON: reasonCode,
                TAX_EXEMPT: taxExempt,
                TAX_DOC: taxDoc,
              },
            });
          } else {
            this.$store.dispatch('changeSettingValues', [{
              field: 'partnerSettings',
              value: {
                ...this.partnerSettings,
                TAX_EXEMPT_EXPIRATION: expireDate,
                TAX_EXEMPT_NUMBER: taxNumber,
                TAX_EXEMPT_REASON: reasonCode,
                TAX_EXEMPT: taxExempt,
                TAX_DOC: taxDoc,
              },
            }]);
          }
        } else {
          await this.update({
            TAX_EXEMPT_EXPIRATION: expireDate,
            TAX_EXEMPT_NUMBER: taxNumber,
            TAX_EXEMPT_REASON: reasonCode,
            TAX_EXEMPT: taxExempt,
          });
        }
        this.updating = false;
      }
    },
  },
};
</script>
